/*
 * barcode-scanner.js
 * Boton dentro del search del header abre overlay con camara y decodifica
 * codigo de barras (EAN-13, EAN-8, UPC-A, Code128) usando la API nativa
 * BarcodeDetector. Al detectar:
 *   - Predictive Search API busca el codigo en Shopify
 *   - 1 resultado: redirect a /products/{handle}
 *   - 0 o varios: redirect a /search?q={codigo}
 *
 * Requiere HTTPS (la tienda ya lo tiene). Si el navegador no soporta
 * BarcodeDetector (Safari < 17, Firefox sin flag), oculta el boton.
 */
(function () {
  'use strict';

  const HAS_NATIVE = typeof window.BarcodeDetector !== 'undefined';
  const FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39'];
  // Fallback ZXing-WASM (universal, funciona en Safari iOS)
  const ZXING_CDN = 'https://cdn.jsdelivr.net/npm/@zxing/browser@0.1.5/+esm';

  const triggers = document.querySelectorAll('[data-bcs-trigger]');
  const overlay  = document.querySelector('[data-barcode-overlay]');
  const video    = overlay && overlay.querySelector('[data-bcs-video]');
  const status   = overlay && overlay.querySelector('[data-bcs-status]');
  const btnClose = overlay && overlay.querySelector('[data-bcs-close]');

  if (!triggers.length || !overlay) {
    console.warn('barcode-scanner: triggers=' + triggers.length + ' overlay=' + !!overlay);
    return;
  }

  console.info('barcode-scanner: native BarcodeDetector =', HAS_NATIVE);

  let stream = null;
  let detector = null;       // BarcodeDetector nativo si existe
  let zxingReader = null;    // BrowserMultiFormatReader si caemos a ZXing
  let zxingControls = null;  // controls.stop() para limpiar
  let raf = null;
  let detected = false;

  function setStatus(msg, kind) {
    status.textContent = msg;
    status.removeAttribute('data-error');
    status.removeAttribute('data-success');
    if (kind === 'error') status.setAttribute('data-error', '');
    if (kind === 'success') status.setAttribute('data-success', '');
  }

  async function start() {
    detected = false;
    overlay.hidden = false;
    setStatus('Solicitando acceso a la cámara…');

    if (HAS_NATIVE) {
      await startNative();
    } else {
      await startZxingFallback();
    }
  }

  async function startNative() {
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false,
      });
    } catch (e) {
      setStatus('No se pudo abrir la cámara. Verifica los permisos.', 'error');
      console.warn('camera fail', e);
      return;
    }

    video.srcObject = stream;
    await video.play().catch(() => {});

    try {
      detector = new window.BarcodeDetector({ formats: FORMATS });
    } catch (e) {
      detector = new window.BarcodeDetector();
    }

    setStatus('Apunta la cámara al código de barras del producto.');
    loop();
  }

  async function startZxingFallback() {
    setStatus('Cargando lector de códigos…');
    let ZX;
    try {
      ZX = await import(ZXING_CDN);
    } catch (e) {
      setStatus('Tu navegador no soporta lectura de códigos. Usa Chrome o actualiza Safari.', 'error');
      console.error('zxing load fail', e);
      return;
    }

    try {
      zxingReader = new ZX.BrowserMultiFormatReader();
      setStatus('Apunta la cámara al código de barras del producto.');
      zxingControls = await zxingReader.decodeFromVideoDevice(
        undefined, video, (result, err, controls) => {
          if (detected) return;
          if (result) {
            detected = true;
            onDetected({ rawValue: result.getText() });
          }
        }
      );
    } catch (e) {
      setStatus('No se pudo abrir la cámara. Verifica los permisos.', 'error');
      console.error('zxing start fail', e);
    }
  }

  async function loop() {
    if (detected || !stream) return;
    if (video.readyState >= 2) {
      try {
        const barcodes = await detector.detect(video);
        if (barcodes && barcodes.length) {
          detected = true;
          onDetected(barcodes[0]);
          return;
        }
      } catch (e) {
        // Errores transitorios de detect() — seguimos en el loop
      }
    }
    raf = requestAnimationFrame(loop);
  }

  function onDetected(barcode) {
    const raw = (barcode.rawValue || '').trim();
    if (!raw) {
      setStatus('Código vacío, intenta de nuevo.', 'error');
      detected = false;
      raf = requestAnimationFrame(loop);
      return;
    }

    setStatus('Código detectado: ' + raw + '. Buscando…', 'success');
    if (navigator.vibrate) navigator.vibrate(80);

    lookupAndRedirect(raw);
  }

  async function lookupAndRedirect(code) {
    try {
      // IMPORTANTE: incluir variants.barcode y variants.sku en fields —
      // sin esos, Predictive Search NO indexa esos campos.
      const url = '/search/suggest.json?q=' + encodeURIComponent(code)
        + '&resources[type]=product'
        + '&resources[limit]=5'
        + '&resources[options][unavailable_products]=last'
        + '&resources[options][fields]=title,vendor,product_type,variants.title,variants.sku,variants.barcode';
      const r = await fetch(url, { headers: { Accept: 'application/json' } });
      const d = await r.json();
      const products = (d.resources && d.resources.results && d.resources.results.products) || [];

      if (products.length === 1) {
        window.location.href = '/products/' + products[0].handle;
      } else if (products.length > 1) {
        window.location.href = '/search?q=' + encodeURIComponent(code);
      } else {
        setStatus('No se encontró un producto con ese código. Probando búsqueda general…', 'error');
        setTimeout(() => {
          window.location.href = '/search?q=' + encodeURIComponent(code);
        }, 1200);
      }
    } catch (e) {
      setStatus('Error consultando el catálogo, intentando búsqueda general…', 'error');
      console.warn('lookup fail', e);
      setTimeout(() => {
        window.location.href = '/search?q=' + encodeURIComponent(code);
      }, 1200);
    }
  }

  function stop() {
    if (raf) { cancelAnimationFrame(raf); raf = null; }
    if (zxingControls && typeof zxingControls.stop === 'function') {
      try { zxingControls.stop(); } catch (e) {}
    }
    zxingControls = null;
    zxingReader = null;
    if (stream) {
      stream.getTracks().forEach(t => t.stop());
      stream = null;
    }
    if (video) video.srcObject = null;
    overlay.hidden = true;
    detected = false;
  }

  triggers.forEach(t => t.addEventListener('click', e => { e.preventDefault(); e.stopPropagation(); start(); }));
  btnClose.addEventListener('click', e => { e.preventDefault(); stop(); });
  overlay.addEventListener('click', e => {
    if (e.target === overlay) stop();
  });
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && !overlay.hidden) stop();
  });
})();
